import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  ApiCustomerBank,
  BankDetailsValue,
  customerBankCode,
  customerBankLabel,
  emptyBankDetails,
  parseBankDetails,
} from '../../../../../core/models/bank-details.models';

@Component({
  selector: 'app-subscription-bank-details',
  templateUrl: './subscription-bank-details.component.html',
  styleUrls: ['./subscription-bank-details.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SubscriptionBankDetailsComponent),
      multi: true,
    },
  ],
})
export class SubscriptionBankDetailsComponent implements ControlValueAccessor {
  @Input() banks: ApiCustomerBank[] = [];
  @Input() fieldId = '';

  value: BankDetailsValue = emptyBankDetails();
  disabled = false;

  private onChange: (value: BankDetailsValue) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(raw: unknown): void {
    this.value = parseBankDetails(raw);
  }

  registerOnChange(fn: (value: BankDetailsValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onEnabledChange(enabled: boolean): void {
    this.emit({ ...this.value, enabled });
  }

  onBankSelect(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    this.onBankChange(target?.value ?? '');
  }

  onAccountInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.onAccountChange(target?.value ?? '');
  }

  onBankChange(bankId: string): void {
    const bank = this.banks.find((item) => customerBankCode(item) === bankId);
    this.emit({
      ...this.value,
      bankId,
      bankLabel: bank ? customerBankLabel(bank) : '',
    });
  }

  onAccountChange(accountNumber: string): void {
    this.emit({ ...this.value, accountNumber });
  }

  markTouched(): void {
    this.onTouched();
  }

  bankCode(bank: ApiCustomerBank): string {
    return customerBankCode(bank);
  }

  bankLabel(bank: ApiCustomerBank): string {
    return customerBankLabel(bank);
  }

  private emit(next: BankDetailsValue): void {
    this.value = next;
    this.onChange(next);
    this.onTouched();
  }
}
